// A promo code carried by the booking link: `?promo=LAUNCH25` (or the embed's
// data-promo). A venue mails its list one link, and the checkout applies the
// code for the guest instead of asking them to type it.
//
// Only a promo code (voucher) is ever applied from a link, never a gift card:
// a gift-card code is a bearer secret, and a URL lands in browser history,
// server logs and forwarded mails. The guest can still type a gift card in.
//
// Nothing is stored: the link is the source. Paystack returns to the page the
// guest paid from (callback_url is the page's own URL), so `?promo=` is still
// there for a checkout reopened after an unpaid trip.

/** Longest code a link may carry; the back office's codes are far shorter. */
export const LINK_PROMO_MAX_LENGTH = 64;

/**
 * The code a link carries, as the back office stores it (trimmed, upper
 * case; validate_voucher compares upper(trim(code))), or null when there is
 * none or it cannot be a code (too long, control characters).
 */
export function normaliseLinkPromo(raw: string | null | undefined): string | null {
  const code = (raw ?? "").trim().toUpperCase();
  if (!code || code.length > LINK_PROMO_MAX_LENGTH) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(code)) return null;
  return code;
}

// Per venue: two widgets for two venues on one page each keep their own.
const linkCodes = new Map<string, string>();

/**
 * The page's own `?promo=` belongs to the one venue the page is for. With
 * widgets for two or more venues on the page it goes to none of them (each
 * embed can carry its own data-promo): one venue's code must never be tried,
 * or matched by name, at another venue's checkout.
 */
export function pagePromoFor(
  raw: string | null | undefined,
  pageTenantSlugs: readonly string[],
): string | null | undefined {
  return new Set(pageTenantSlugs).size === 1 ? raw : undefined;
}

/** Called once per mount with the embed's data-promo or the page's ?promo=. */
export function rememberLinkPromo(tenantSlug: string, raw: string | null | undefined): void {
  const code = normaliseLinkPromo(raw);
  if (tenantSlug && code) linkCodes.set(tenantSlug, code);
}

/** The code this venue's link carried, if any. */
export function linkPromoFor(tenantSlug: string): string | null {
  return linkCodes.get(tenantSlug) ?? null;
}

/**
 * Whether the checkout should apply the link's code now: there is one, this
 * checkout takes codes (the same rule that shows the code box), something is
 * charged now, it has not been tried in this checkout yet (a guest who
 * removes it keeps it removed; one that failed is not retried on every
 * render), and no code is applied.
 *
 * Nothing charged now (a free ticket, pay at the venue): a code there takes
 * the cart down the code-covered R0 path, which redeems it — spending the
 * guest's one use of the campaign on a booking it took nothing off.
 */
export function shouldApplyLinkPromo(input: {
  code: string | null;
  codesAccepted: boolean;
  chargedNowCents: number;
  alreadyTried: boolean;
  appliedCount: number;
}): boolean {
  return (
    input.code !== null &&
    input.codesAccepted &&
    input.chargedNowCents > 0 &&
    !input.alreadyTried &&
    input.appliedCount === 0
  );
}

/**
 * The booking link's code, checked before checkout: what the checkout will
 * take off (link-promo-preview.tsx asks validate_voucher, the same read-only
 * check the checkout runs, as soon as a ride is held).
 *   discountCents   what comes off, never more than chargedNowCents
 *   chargedNowCents what the checkout charges now before it (its own per-line
 *                   amounts: checkoutLineCents)
 */
export type LinkPromoPreview = {
  code: string;
  discountCents: number;
  chargedNowCents: number;
  /** Every line charged now is charged its whole price (none is a deposit). */
  fullPrice: boolean;
  /** The services of the lines charged now. */
  serviceIds: readonly string[];
};

/**
 * A total the visit shows, with the link's discount taken off; null to show
 * it as it is. Only when that total IS what the checkout charges now: a
 * deposit, a line held at R0 or another service's lines make it a different
 * figure, and taking the discount off it would show a price nobody pays.
 */
export function totalAfterLinkPromo(
  totalCents: number,
  preview: LinkPromoPreview | null,
  scope: {
    /** A visit's price (true): only when every line charged now is charged in full. The drawer's pay-now total (false): what is charged now, deposits included. */
    fullPriceOnly?: boolean;
    /** One service's own price: only when every line charged now is that service's. */
    serviceId?: string;
  } = {},
): number | null {
  if (!preview || preview.discountCents <= 0) return null;
  if (Math.round(totalCents) !== preview.chargedNowCents) return null;
  if ((scope.fullPriceOnly ?? true) && !preview.fullPrice) return null;
  if (scope.serviceId !== undefined && !preview.serviceIds.every((id) => id === scope.serviceId)) return null;
  return Math.max(0, preview.chargedNowCents - preview.discountCents);
}

/** What get_promo_link_terms says about the link's code (state/booking.ts fetchPromoLinkTerms). */
export type PromoLinkTerms = {
  code: string;
  kind: "percent" | "fixed_amount" | "free_addon";
  discount_percent: number | null;
  applies_to_service_ids: readonly string[];
  applies_to_service_types: readonly string[];
  min_spend_cents: number | null;
  deposit_service_ids: readonly string[];
  /** Services whose advance-deposit policy can defer payment: checkout then applies no code. */
  deferred_deposit_service_ids?: readonly string[];
};

/**
 * A ride the guest is still picking (nothing held yet), priced with the
 * link's code: its total after the code, or null to show it as it is. Only a
 * PERCENTAGE (a fixed amount comes off the whole visit, not one line), only
 * for a service the code applies to, never one that takes a deposit (the
 * percentage then comes off the deposit, not this price) or can defer it (no
 * code is applied then), and not below the code's minimum spend when this
 * line will be the visit's first booking (the code is judged on that one; a
 * visit whose first booking already took the code passes minSpend: false).
 * Rounded as validate_voucher rounds.
 */
export function pickedLineAfterLinkPromo(
  terms: PromoLinkTerms | null,
  line: { serviceId: string; serviceType: string; totalCents: number },
  { minSpend = true }: { minSpend?: boolean } = {},
): number | null {
  if (!terms || terms.kind !== "percent" || !terms.discount_percent || terms.discount_percent <= 0) return null;
  if (line.totalCents <= 0) return null;
  if (terms.applies_to_service_ids.length > 0 && !terms.applies_to_service_ids.includes(line.serviceId)) return null;
  if (terms.applies_to_service_types.length > 0 && !terms.applies_to_service_types.includes(line.serviceType)) return null;
  if (terms.deposit_service_ids.includes(line.serviceId)) return null;
  if ((terms.deferred_deposit_service_ids ?? []).includes(line.serviceId)) return null;
  if (minSpend && terms.min_spend_cents != null && line.totalCents < terms.min_spend_cents) return null;
  const off = Math.round((line.totalCents * Math.min(terms.discount_percent, 100)) / 100);
  return Math.max(0, line.totalCents - off);
}
